import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  TextField,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import PaysGrid from '../components/PaysGrid';
import CardSkeleton from '../components/CardSkeleton';
import { useSnackbar } from '../components/SnackbarProvider';
import { recupererListeVisas, recupererListePays } from '../Services';
import { CarteProgression, compterVoyageurs, estRouteDeclaree, useOnboarding } from '../onboarding';
import DestinationIndisponibleDialog from '../components/DestinationIndisponibleDialog';
import { Visas } from '../models/Visas';

export interface Pays {
  id: number;
  libelle: string;
  visible: string;
  url_infos: string;
  url_flag: string;
  url_image: string;
}

const VisasScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { showSnackbar } = useSnackbar();
  const { memoriserVisa } = useOnboarding();

  const [pays, setPays] = useState<Pays[]>([]);
  const [paysVisibles, setPaysVisibles] = useState<Pays[]>([]);
  const [paysSelectionne, setPaysSelectionne] = useState<number | null>(null);
  const [visasFiltres, setVisasFiltres] = useState<Visas[]>([]);
  const [urlInfoDestination, setUrlInfoDestination] = useState<string | null>(null);
  const [isLegrandUser, setIsLegrandUser] = useState<boolean>(false);
  const [isLoadingPays, setIsLoadingPays] = useState<boolean>(true);
  const [isLoadingVisas, setIsLoadingVisas] = useState<boolean>(false);
  const [filtreType, setFiltreType] = useState<'tous' | 'affaire' | 'tourisme'>('tous');
  const [openTooltipId, setOpenTooltipId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [destinationIndisponible, setDestinationIndisponible] = useState<string | null>(null);
  const [paysRecents, setPaysRecents] = useState<number[]>(() => {
    try {
      const stored = localStorage.getItem('recentPays');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const getUrlInfos = () => {
    try {
      const url = localStorage.getItem('pays_url_infos');
      if (url) {
        setUrlInfoDestination(url);
      }
    } catch (e) {
      console.error("Erreur lors de la récupération de l'URL depuis localStorage:", e);
    }
  };

  const handlePaysSelect = (paysId: number | null) => {
    const selectedPays = pays.find((p) => p.id === paysId);
    if (selectedPays) {
      setPaysSelectionne(selectedPays.id);
      try {
        localStorage.setItem('pays_url_infos', selectedPays.url_infos + '?appli=yes');
        console.log('URL du pays sauvegardée dans localStorage:', selectedPays.url_infos);
        getUrlInfos();
      } catch (e) {
        console.error("Erreur lors de la sauvegarde de l'URL dans localStorage:", e);
      }
      // Save to recent countries list
      try {
        const updated = [selectedPays.id, ...paysRecents.filter((id) => id !== selectedPays.id)].slice(0, 4);
        setPaysRecents(updated);
        localStorage.setItem('recentPays', JSON.stringify(updated));
      } catch (e) {
        console.error('Erreur lors de la sauvegarde des pays récents:', e);
      }
    }
  };

  // Résolution « à blanc » : permet de rejouer navigateBasedOnTitle SANS naviguer,
  // afin de connaître la route cible avant toute autre décision.
  const resolutionRef = useRef<{ actif: boolean; route: string | null; options?: any }>({
    actif: false,
    route: null,
  });

  // Remplace navigate() dans navigateBasedOnTitle. Signature strictement identique,
  // pour qu'aucun changement de sémantique ne soit possible lors du renommage.
  const allerVers = (route: string, options?: any) => {
    if (resolutionRef.current.actif) {
      resolutionRef.current.route = route;
      resolutionRef.current.options = options;
      return;
    }
    navigate(route, options);
  };

  // Navigation en fonction du titre du visa et du champ "classique"
  const navigateBasedOnTitle = (
    title: string,
    prixconsulat: number,
    prixintervention: number,
    url_flag: string,
    validite: string,
    informations: string,
    classique: number,
    commentaires: string
  ) => {
    if (classique === 1 && title.includes('Arabie Saoudite')) {
      allerVers('/classique_formulaire_arabie_saoudite', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Bangladesh')) {
      allerVers('/classique_formulaire_bangladesh', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Centrafrique')) {
      allerVers('/classique_formulaire_centrafrique', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Congo Brazzaville')) {
      allerVers('/classique_formulaire_congo_brazzaville', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Congo Kinshasa')) {
      allerVers('/classique_formulaire_congo_kinshasa', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Gabon')) {
      allerVers('/classique_formulaire_gabon', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Ghana')) {
      allerVers('/classique_formulaire_ghana', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Liban')) {
      allerVers('/classique_formulaire_liban', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Libéria')) {
      allerVers('/classique_formulaire_Liberia', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Népal')) {
      allerVers('/classique_formulaire_Nepal', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Soudan')) {
      allerVers('/classique_formulaire_Soudan', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Chine') && !title.includes('Chine eCard')) {
      allerVers('/classique_formulaire_Chine', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    // Corée du Sud : le visa classique (dossier au consulat) a son propre
    // formulaire, seul à porter les services optionnels payants. Sans cette
    // branche il tombait dans la cascade e-visa ci-dessous et les perdait.
    // ⚠️ À laisser AVANT la branche Nigéria : c'est à elle qu'est rattaché
    // le `else if` qui ouvre tout le routage e-visa.
    if (classique === 1 && title.includes('Corée Du Sud')) {
      allerVers('/classique_formulaire_coree_du_sud', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Ethiopie')) {
      allerVers('/classique_formulaire_Ethiopie', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Guinée Bissau')) {
      allerVers('/classique_formulaire_guinee_bissau', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Guinée Equatoriale')) {
      allerVers('/classique_formulaire_guinee_equatoriale', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && title.includes('Madagascar')) {
      allerVers('/classique_formulaire_madagascar', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    if (classique === 1 && (title.includes('Nigéria') || title.includes('Nigeria'))) {
      allerVers('/classique_formulaire_nigeria', {
        state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires },
      });
      return;
    }
    else if (title.includes('Chine eCard')) {
      allerVers('/formulaire_chine_ecard', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Inde eCard')) {
      allerVers('/formulaire_inde_ecard', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    // Singapour et Malaisie : test insensible à la casse ET à l'ordre des mots, le
    // libellé du visa pouvant être saisi « Singapour eCard » comme « eCard Singapour ».
    // À garder avant la branche générique Singapour plus bas, qui capterait sinon.
    } else if (/singapour/i.test(title) && /e-?card/i.test(title)) {
      allerVers('/formulaire_singapour_ecard', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (/malaisie/i.test(title) && /e-?card/i.test(title)) {
      allerVers('/formulaire_malaisie_ecard', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Inde')) {
      allerVers('/formulaire_inde', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Nigéria') || title.includes('Nigeria')) {
      allerVers('/formulaire_nigeria', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('USA')) {
      allerVers('/formulaire_usa', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Angola')) {
      allerVers('/formulaire_angola', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Arabie Saoudite')) {
      allerVers('/formulaire_arabie_saoudite', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Australie')) {
      allerVers('/formulaire_australie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Azerbaidjan')) {
      allerVers('/formulaire_azerbaidjan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Bahreïn')) {
      allerVers('/formulaire_bahrein', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Benin')) {
      allerVers('/formulaire_benin', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Burkina Faso')) {
      allerVers('/formulaire_burkina_faso', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Cambodge')) {
      allerVers('/formulaire_cambodge', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Cameroun')) {
      allerVers('/formulaire_cameroun', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Canada')) {
      allerVers('/formulaire_canada', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Cap-Vert')) {
      allerVers('/formulaire_cap_vert', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes('Corée Du Sud')) {
      allerVers('/formulaire_coree_du_sud', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Cuba")) {
      allerVers('/formulaire_cuba', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Djibouti")) {
      allerVers('/formulaire_djibouti', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Dubai")) {
      allerVers('/formulaire_dubai', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Egypte")) {
      allerVers('/formulaire_egypte', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Ethiopie")) {
      allerVers('/formulaire_ethiopie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Gabon")) {
      allerVers('/formulaire_gabon', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Géorgie")) {
      allerVers('/formulaire_georgie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Guinée") && !title.includes("Papouasie")) {
      allerVers('/formulaire_guinee', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Indonésie")) {
      allerVers('/formulaire_indonesie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Jordanie")) {
      allerVers('/formulaire_jordanie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Kenya")) {
      allerVers('/formulaire_kenya', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Kirghizistan")) {
      allerVers('/formulaire_kirghizistan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Koweït")) {
      allerVers('/formulaire_koweit', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Laos")) {
      allerVers('/formulaire_laos', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Lesotho")) {
      allerVers('/formulaire_lesotho', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Madagascar")) {
      allerVers('/formulaire_madagascar', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Malawi")) {
      allerVers('/formulaire_malawi', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Mongolie")) {
      allerVers('/formulaire_mongolie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Mozambique")) {
      allerVers('/formulaire_mozambique', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Myanmar")) {
      allerVers('/formulaire_myanmar', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Nouvelle-Zélande")) {
      allerVers('/formulaire_nouvelle_zelande', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Oman")) {
      allerVers('/formulaire_oman', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Ouganda")) {
      allerVers('/formulaire_ouganda', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Ouzbékistan")) {
      allerVers('/formulaire_ouzbekistan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Pakistan")) {
      allerVers('/formulaire_pakistan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Papouasie")) {
      allerVers('/formulaire_papouasie_ng', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("République Dominicaine")) {
      allerVers('/formulaire_republique_dominicaine', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Seychelles")) {
      allerVers('/formulaire_seychelles', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Sierra Leone")) {
      allerVers('/formulaire_sierra_leone', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Singapour")) {
      allerVers('/formulaire_singapour', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Sri Lanka")) {
      allerVers('/formulaire_sri_lanka', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Tadjikistan")) {
      allerVers('/formulaire_tadjikistan', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Tanzanie")) {
      allerVers('/formulaire_tanzanie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Thailande")) {
      allerVers('/formulaire_thailande', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Vietnam")) {
      allerVers('/formulaire_vietnam', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Zambie")) {
      allerVers('/formulaire_zambie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Zimbabwe")) {
      allerVers('/formulaire_zimbabwe', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Togo")) {
      allerVers('/formulaire_togo', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Turquie")) {
      allerVers('/formulaire_turquie', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else if (title.includes("Royaume-Uni") || title.includes("UK")) {
      allerVers('/formulaire_uk', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    }
    else if (title.includes("Côte d'Ivoire")) {
      allerVers('/formulaire_cote_d_ivoire', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    } else {
      allerVers('/formulaire_template', { state: { title, prixconsulat, prixintervention, url_flag, validite, informations, commentaires } });
    }
  };

  /**
   * Rejoue navigateBasedOnTitle en mode résolution et renvoie la route qu'il aurait
   * empruntée, sans naviguer. Indispensable pour contrôler la destination AVANT
   * de savoir si l'utilisateur possède un voyageur.
   */
  const resoudreRoute = (
    title: string,
    prixconsulat: number,
    prixintervention: number,
    url_flag: string,
    validite: string,
    informations: string,
    classique: number,
    commentaires: string
  ): { route: string | null; options?: any } => {
    resolutionRef.current = { actif: true, route: null, options: undefined };
    try {
      navigateBasedOnTitle(
        title,
        prixconsulat,
        prixintervention,
        url_flag,
        validite,
        informations,
        classique,
        commentaires
      );
      return { route: resolutionRef.current.route, options: resolutionRef.current.options };
    } finally {
      resolutionRef.current = { actif: false, route: null, options: undefined };
    }
  };

  const checkVoyageursAndNavigate = async (
    title: string,
    prixconsulat: number,
    prixintervention: number,
    url_flag: string,
    validite: string,
    informations: string,
    classique: number,
    commentaires: string
  ) => {
    const userJson = localStorage.getItem('user');
    if (!userJson) {
      showSnackbar('Utilisateur non trouvé.', 'error');
      return;
    }

    let user: any;
    try {
      user = JSON.parse(userJson);
    } catch (e) {
      showSnackbar('Votre session a expiré. Reconnectez-vous pour continuer.', 'error');
      navigate('/login', { replace: true });
      return;
    }

    // 1) La destination est-elle réellement ouverte en ligne ?
    //    Ce contrôle passe AVANT la question du voyageur. Placé après, un
    //    utilisateur sans voyageur ne découvrait la page blanche qu'une fois tout
    //    son profil saisi, documents compris.
    const cible = resoudreRoute(
      title,
      prixconsulat,
      prixintervention,
      url_flag,
      validite,
      informations,
      classique,
      commentaires
    );
    if (!estRouteDeclaree(cible.route)) {
      setDestinationIndisponible(title);
      return;
    }
    const route = cible.route as string;

    // Mémorise le visa choisi AVANT l'aiguillage : que l'on parte créer un voyageur
    // ou droit au formulaire, la feuille de route doit pouvoir y ramener.
    memoriserVisa({
      titre: title,
      route,
      paysLibelle: pays.find((p) => p.id === paysSelectionne)?.libelle ?? '',
      urlFlag: url_flag,
      state: (cible.options?.state ?? {}) as Record<string, any>,
    });

    // 2) L'utilisateur a-t-il au moins un voyageur ?
    //    Appel STRICT : recupererListeVoyageurs renvoie [] sur erreur réseau, ce qui
    //    enverrait créer un voyageur à un client qui en a déjà cinq.
    let aUnVoyageur: boolean | null;
    try {
      aUnVoyageur = (await compterVoyageurs(user.id_utilisateur)) > 0;
    } catch (e) {
      // Backend injoignable : on ne conclut rien et on laisse passer.
      // Le sélecteur de voyageur du formulaire prendra le relais.
      aUnVoyageur = null;
    }

    if (aUnVoyageur === false) {
      // Le visa choisi était purement perdu : la redirection se faisait sans
      // returnUrl, alors que VoyageurInsert le prend en charge (l.203).
      // On dépose aussi le contexte du visa dans sessionStorage : c'est le contrat
      // qu'attendent les 56 formulaires au retour, via ?fromCreation=true.
      try {
        sessionStorage.setItem('formState', JSON.stringify(cible.options?.state ?? {}));
      } catch (e) {
        console.warn('Impossible de mémoriser le visa choisi.', e);
      }
      showSnackbar(
        "Créez d'abord le profil du voyageur : nous vous ramènerons ensuite sur ce visa.",
        'info'
      );
      navigate(
        `/formulaire_creation_voyageur?onboarding=1&returnUrl=${encodeURIComponent(route)}`
      );
      return;
    }

    navigate(route, cible.options);
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      const email = user.email || '';
      if (email.endsWith('@legrand.com') || email.endsWith('@legrand.fr')) {
        setIsLegrandUser(true);
      }
    }

    const fetchPays = async () => {
      setIsLoadingPays(true);
      const paysListe = await recupererListePays();
      setPays(paysListe);
      const visiblePays = paysListe.filter((p) => p.visible === 'oui');
      setPaysVisibles(visiblePays);
      setIsLoadingPays(false);
    };
    fetchPays();
  }, []);

  useEffect(() => {
    const fetchVisasFiltres = async () => {
      if (paysSelectionne !== null) {
        setIsLoadingVisas(true);
        const visasListe = await recupererListeVisas();
        const filtered = visasListe.filter((v) => v.id_pays === paysSelectionne);
        setVisasFiltres(filtered);
        setIsLoadingVisas(false);
      }
    };
    fetchVisasFiltres();
  }, [paysSelectionne]);

  const handleRetourPays = () => {
    setPaysSelectionne(null);
    setFiltreType('tous');
    setSearchQuery('');
  };

  const getVisasFiltresParType = () => {
    if (filtreType === 'tous') {
      return visasFiltres;
    } else if (filtreType === 'affaire') {
      return visasFiltres.filter((v) => v.titre.toLowerCase().includes('affaire'));
    } else if (filtreType === 'tourisme') {
      return visasFiltres.filter((v) => v.titre.toLowerCase().includes('tourisme'));
    }
    return visasFiltres;
  };

  const visasAffiches = getVisasFiltresParType();

  // Non-visible countries
  const paysNonVisibles = pays.filter((p) => p.visible !== 'oui');

  // Filter countries by search query (search across ALL countries)
  const query = searchQuery.trim().toLowerCase();
  const paysRecherches = query
    ? paysVisibles.filter((p) => p.libelle.toLowerCase().includes(query))
    : paysVisibles;
  const paysAutresRecherches = query
    ? paysNonVisibles.filter((p) => p.libelle.toLowerCase().includes(query))
    : paysNonVisibles;

  // Get selected country info
  const selectedPaysInfo = pays.find((p) => p.id === paysSelectionne);

  const openInfosPays = () => {
    if (urlInfoDestination) {
      window.open(urlInfoDestination, '_blank');
    } else {
      showSnackbar('Aucune URL disponible.', 'info');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)',
        backgroundColor: 'transparent',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflowY: 'auto',
      }}
    >
      {/* Header (mobile only - returns null on desktop) */}
      <Header />

      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
        {paysSelectionne ? (
          /* ========== VUE VISAS (pays sélectionné) ========== */
          <>
            {/* Toolbar */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mb: 3,
                p: { xs: 1.5, sm: 2 },
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {/* Row 1: Back + Country info + Info button */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flex: { xs: 'unset', sm: 1 },
                }}
              >
                <IconButton
                  onClick={handleRetourPays}
                  sx={{
                    backgroundColor: '#2e7d32',
                    color: 'white',
                    width: 40,
                    height: 40,
                    '&:hover': { backgroundColor: '#1b5e20' },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>

                {selectedPaysInfo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img
                      src={selectedPaysInfo.url_flag}
                      alt={selectedPaysInfo.libelle}
                      style={{ width: 28, height: 20, objectFit: 'cover', borderRadius: 2 }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1rem', sm: '1.15rem' },
                        color: '#333',
                      }}
                    >
                      {selectedPaysInfo.libelle}
                    </Typography>
                  </Box>
                )}

                <IconButton
                  onClick={openInfosPays}
                  sx={{
                    ml: 'auto',
                    color: '#2e7d32',
                    display: { xs: 'flex', sm: 'none' },
                  }}
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </Box>

              {/* Filters */}
              <ToggleButtonGroup
                value={filtreType}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    setFiltreType(newValue);
                  }
                }}
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  border: 'none',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 'none',
                  },
                  '& .MuiToggleButton-root': {
                    px: 2.5,
                    py: 0.8,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    borderRadius: '8px !important',
                    flex: { xs: 1, sm: 'none' },
                    minWidth: { xs: 'auto', sm: '90px' },
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      backgroundColor: '#2e7d32',
                      color: 'white',
                      boxShadow: '0 2px 8px rgba(46,125,50,0.3)',
                      '&:hover': {
                        backgroundColor: '#1b5e20',
                      },
                    },
                    '&:not(.Mui-selected)': {
                      color: '#666',
                      '&:hover': {
                        backgroundColor: '#ebebeb',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="tous">Tous</ToggleButton>
                <ToggleButton value="affaire">Affaire</ToggleButton>
                <ToggleButton value="tourisme">Tourisme</ToggleButton>
              </ToggleButtonGroup>

              {/* Infos Pays button (desktop only) */}
              <Button
                variant="outlined"
                startIcon={<InfoOutlinedIcon />}
                onClick={openInfosPays}
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  borderColor: '#2e7d32',
                  color: '#2e7d32',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#1b5e20',
                    backgroundColor: 'rgba(46,125,50,0.04)',
                  },
                }}
              >
                Infos Pays
              </Button>
            </Box>

            {/* Visa Cards Grid */}
            {isLoadingVisas ? (
              <CardSkeleton count={4} variant="visa" />
            ) : (
            <Grid container spacing={2}>
              {visasAffiches.map((item, index) => {
                const isEVisa = item.titre.toLowerCase().includes('evisa');
                const isECard = item.titre.toLowerCase().includes('ecard');
                const isClassique = item.classique === 1;
                const tooltipMessage = isEVisa
                  ? "Toute la procédure est en ligne, aucun document n'est à envoyer par voie postale."
                  : "Cette demande doit être traitée par le consulat, le dossier devra nous parvenir à notre adresse postale.";

                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: isDesktop ? 'translateY(-4px)' : 'none',
                          boxShadow: isDesktop
                            ? '0 8px 30px rgba(0,0,0,0.15)'
                            : '0 2px 12px rgba(0,0,0,0.08)',
                        },
                      }}
                      onClick={() =>
                        checkVoyageursAndNavigate(
                          item.titre,
                          item.prixconsulat ?? 0,
                          item.prixintervention ?? 0,
                          item.url,
                          item.validite,
                          item.informations ?? '',
                          item.classique ?? 0,
                          item.commentaires ?? ''
                        )
                      }
                    >
                      {/* Badge eVisa / Classique */}
                      {!isECard && (
                        <Chip
                          label={isClassique ? 'Classique' : 'eVisa'}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            zIndex: 1,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            backgroundColor: isClassique ? '#ff9800' : '#2e7d32',
                            color: 'white',
                          }}
                        />
                      )}

                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ position: 'relative', mb: 1 }}>
                          <Typography
                            variant="h6"
                            align="center"
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '0.95rem', sm: '1.05rem' },
                              color: '#333',
                              px: 3,
                            }}
                          >
                            {item.titre}
                          </Typography>
                          {!isECard && (
                            <Tooltip
                              title={tooltipMessage}
                              arrow
                              placement="top"
                              open={openTooltipId === index}
                              onClose={() => setOpenTooltipId(null)}
                              disableHoverListener
                              disableFocusListener
                              disableTouchListener
                            >
                              <IconButton
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  color: 'primary.main',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenTooltipId(openTooltipId === index ? null : index);
                                }}
                              >
                                <HelpOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Typography
                          variant="body2"
                          align="center"
                          sx={{ color: '#666', mb: 1.5 }}
                        >
                          {item.validite}
                        </Typography>

                        <CardMedia
                          component="img"
                          image={item.url}
                          alt={item.titre}
                          sx={{
                            height: { xs: 120, sm: 140, md: 150 },
                            objectFit: 'contain',
                            my: 1,
                            borderRadius: 2,
                          }}
                        />

                        {!isLegrandUser && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: 2,
                              mt: 1.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Chip
                              label={`Visa: ${item.prixconsulat}€`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                borderColor: '#2e7d32',
                                color: '#2e7d32',
                              }}
                            />
                            <Chip
                              label={`Service: ${item.prixintervention}€`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                borderColor: '#1976d2',
                                color: '#1976d2',
                              }}
                            />
                          </Box>
                        )}

                        {item.informations && (
                          <Typography
                            variant="caption"
                            align="center"
                            sx={{
                              display: 'block',
                              mt: 1.5,
                              color: '#888',
                              lineHeight: 1.4,
                            }}
                          >
                            {item.informations}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            )}
          </>
        ) : (
          /* ========== VUE SELECTION DE PAYS ========== */
          <>
            <CarteProgression contexte="destinations" />

            {/* Recently viewed countries */}
            {paysRecents.length > 0 && (
              <Box sx={{ maxWidth: { xs: '100%', sm: 600 }, mx: 'auto', mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'white',
                    textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                    fontWeight: 700,
                    mb: 1,
                    fontSize: '0.9rem',
                  }}
                >
                  Consultés récemment
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    overflowX: 'auto',
                    pb: 0.5,
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.4)', borderRadius: 2 },
                  }}
                >
                  {paysRecents.map((recentId) => {
                    const recentPays = pays.find((p) => p.id === recentId);
                    if (!recentPays) return null;
                    return (
                      <Box
                        key={recentId}
                        onClick={() => handlePaysSelect(recentId)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.5,
                          py: 0.75,
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: 2,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.35)',
                          },
                        }}
                      >
                        <img
                          src={recentPays.url_flag}
                          alt={recentPays.libelle}
                          style={{ width: 22, height: 15, objectFit: 'cover', borderRadius: 2 }}
                        />
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                          }}
                        >
                          {recentPays.libelle}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Search + Dropdown area */}
            <Box
              sx={{
                maxWidth: { xs: '100%', sm: 600 },
                mx: 'auto',
                mb: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* Search bar */}
              <TextField
                placeholder="Rechercher un pays..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    backdropFilter: 'blur(8px)',
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: '2px solid #2e7d32' },
                  },
                }}
              />

              {/* Country dropdown */}
              <TextField
                select
                label="Ou sélectionner un pays"
                value={paysSelectionne || ''}
                onChange={(e) => handlePaysSelect(Number(e.target.value))}
                fullWidth
                SelectProps={{
                  sx: {
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    borderRadius: 3,
                  },
                  renderValue: (value: unknown) => {
                    const selectedPays = pays.find((p) => p.id === value);
                    if (selectedPays) {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img
                            src={selectedPays.url_flag}
                            alt={selectedPays.libelle}
                            style={{ width: 24, height: 16, objectFit: 'cover', borderRadius: 2 }}
                          />
                          <span>{selectedPays.libelle}</span>
                        </Box>
                      );
                    }
                    return '';
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                  },
                }}
              >
                {pays.map((paysItem) => (
                  <MenuItem key={paysItem.id} value={paysItem.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={paysItem.url_flag}
                        alt={paysItem.libelle}
                        style={{ width: 24, height: 16, objectFit: 'cover', borderRadius: 2 }}
                      />
                      <span>{paysItem.libelle}</span>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Countries Grid */}
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              {isLoadingPays ? (
                <CardSkeleton count={8} variant="pays" />
              ) : (
                <PaysGrid paysVisibles={paysRecherches} paysAutres={paysAutresRecherches} onSelectPays={handlePaysSelect} />
              )}
            </Box>
          </>
        )}
      </Box>

      <DestinationIndisponibleDialog
        titreVisa={destinationIndisponible}
        onClose={() => setDestinationIndisponible(null)}
      />
    </Box>
  );
};

export default VisasScreen;
